import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { trackAddToCart } from "@/utils/ga4";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProducts } from "@/contexts/ProductsContext";

export interface CartItem {
 productId: string;
 variantIndex: number | null;
 // ID of the selected nested sub-variant (color/scent). Null when the product
 // (or selected variant) has no sub-variants, or for cart rows created before
 // the sub-variant feature shipped.
 subVariantId?: string | null;
 quantity: number;
}

interface CartContextType {
 items: CartItem[];
 addItem: (productId: string, variantIndex: number | null, subVariantId?: string | null) => void;
 removeItem: (productId: string, variantIndex: number | null, subVariantId?: string | null) => void;
 updateQuantity: (productId: string, variantIndex: number | null, quantity: number, subVariantId?: string | null) => void;
 clearCart: () => void;
 totalItems: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
 const ctx = useContext(CartContext);
 if (!ctx) throw new Error("useCart must be used within CartProvider");
 return ctx;
};

const CART_STORAGE_KEY = "nil_beauty_cart";

const loadCart = (): CartItem[] => {
 try {
 const raw = localStorage.getItem(CART_STORAGE_KEY);
 return raw ? JSON.parse(raw) : [];
 } catch {
 return [];
 }
};

const saveCart = (items: CartItem[]) => {
 localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
 const [items, setItemsRaw] = useState<CartItem[]>(loadCart);
 const { user } = useAuth();
 const { products } = useProducts();
 const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
 const hasMergedServerCart = useRef(false);

 const setItems = (updater: CartItem[] | ((prev: CartItem[]) => CartItem[])) => {
 setItemsRaw((prev) => {
 const next = typeof updater === "function" ? updater(prev) : updater;
 saveCart(next);
 return next;
 });
 };

 // Merge server cart into local on login
 useEffect(() => {
 if (!user || hasMergedServerCart.current) return;
 hasMergedServerCart.current = true;
 (async () => {
 try {
 const { data } = await supabase
 .from("user_carts")
 .select("items, checked_out_at")
 .eq("user_id", user.id)
 .maybeSingle();
 if (data && !data.checked_out_at && Array.isArray(data.items) && data.items.length > 0) {
 const serverItems = data.items as unknown as CartItem[];
 setItems((prev) => {
 // Merge: prefer higher quantity per key
 const map = new Map<string, CartItem>();
 for (const it of [...serverItems, ...prev]) {
  const key = `${it.productId}__${it.variantIndex ?? "d"}__${it.subVariantId ?? "_"}`;
  const existing = map.get(key);
  map.set(key, existing ? { ...it, quantity: Math.max(existing.quantity, it.quantity) } : it);
  }
 return Array.from(map.values());
 });
 }
 } catch {
 // silent
 }
 })();
 }, [user]);

 useEffect(() => {
 if (!user) return;
 if (syncTimer.current) clearTimeout(syncTimer.current);
 syncTimer.current = setTimeout(async () => {
 try {
 // Compute subtotal for email display
 let subtotal = 0;
 for (const it of items) {
 const p = products.find((x) => x.id === it.productId);
 if (!p) continue;
 const price =
 it.variantIndex !== null && p.sizeVariants
 ? p.sizeVariants[it.variantIndex]?.price ?? p.price
 : p.price;
 subtotal += price * it.quantity;
 }

 // Enriched items snapshot for email
 const enrichedItems = items.map((it) => {
 const p = products.find((x) => x.id === it.productId);
 const variant =
 p && it.variantIndex !== null && p.sizeVariants
 ? p.sizeVariants[it.variantIndex]
 : null;
 const sub = variant?.subVariants?.find((sv) => sv.id === it.subVariantId) || null;
 return {
 productId: it.productId,
 variantIndex: it.variantIndex,
 variantId: variant?.id ?? null,
 subVariantId: it.subVariantId ?? null,
 subVariantLabel: sub
 ? [sub.scent || sub.scentAr, sub.poeticName || sub.poeticNameAr].filter(Boolean).join(" · ")
 : null,
 size: variant?.size ?? null,
 label: variant?.label ?? null,
 labelAr: variant?.labelAr ?? null,
 quantity: it.quantity,
 name: p?.name ?? "Product",
 variantLabel: [variant?.size, variant?.label && variant.label !== p?.name ? variant.label : null].filter(Boolean).join(" · ") || null,
 price: variant?.price ?? p?.price ?? 0,
 image: variant?.image ?? p?.image ?? null,
 };
 });

 await supabase.from("user_carts").upsert(
 {
 user_id: user.id,
 items: enrichedItems as any,
 subtotal,
 updated_at: new Date().toISOString(),
 reminder_sent_at: null,
 checked_out_at: null,
 },
 { onConflict: "user_id" }
 );
 } catch {
 // silent, never block UX
 }
 }, 1500);

 return () => {
 if (syncTimer.current) clearTimeout(syncTimer.current);
 };
 }, [items, user, products]);

 const keyOf = (productId: string, variantIndex: number | null, subVariantId?: string | null) =>
  `${productId}__${variantIndex ?? "d"}__${subVariantId ?? "_"}`;

 const matches = (a: CartItem, productId: string, variantIndex: number | null, subVariantId?: string | null) =>
  a.productId === productId
  && a.variantIndex === variantIndex
  && (a.subVariantId ?? null) === (subVariantId ?? null);

 const addItem = (productId: string, variantIndex: number | null, subVariantId: string | null = null) => {
 // GA4: add_to_cart
 const product = products.find((p) => p.id === productId);
 if (product) {
 const variant = variantIndex !== null && product.sizeVariants ? product.sizeVariants[variantIndex] : null;
 const price = variant ? variant.price : product.price;
 trackAddToCart(
 { item_id: productId, item_name: product.name, item_variant: variant?.label, price, quantity: 1 },
 price,
 );
 }

 setItems((prev) => {
 const idx = prev.findIndex((i) => matches(i, productId, variantIndex, subVariantId));
 if (idx >= 0) {
 const updated = [...prev];
 updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
 return updated;
 }
 return [...prev, { productId, variantIndex, subVariantId, quantity: 1 }];
 });
 };

 const removeItem = (productId: string, variantIndex: number | null, subVariantId: string | null = null) => {
 setItems((prev) => prev.filter((i) => !matches(i, productId, variantIndex, subVariantId)));
 };

 const updateQuantity = (productId: string, variantIndex: number | null, quantity: number, subVariantId: string | null = null) => {
 if (quantity <= 0) {
 removeItem(productId, variantIndex, subVariantId);
 return;
 }
 setItems((prev) => {
 const idx = prev.findIndex((i) => matches(i, productId, variantIndex, subVariantId));
 if (idx >= 0) {
 const updated = [...prev];
 updated[idx] = { ...updated[idx], quantity };
 return updated;
 }
 return prev;
 });
 };

 const clearCart = () => setItems([]);

 // Also key map merges on sub-variant to avoid collapsing distinct combos.
 void keyOf;

 const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

 return (
 <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems }}>
 {children}
 </CartContext.Provider>
 );
};
